import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import NewRequestBody from './NewRequestBody';
import ReviewPage from './ReviewPage';
import ThanksPage from './ThanksPage';
import useSyncedState from './useSyncedState';
import V2SyncGate from './V2SyncGate';
import { V1_FEATURES, RedirectSpinner } from './v1_adapter';

function secondsSinceEpoch() {
  return Math.floor(Date.now() / 1000);
}

function NewRequest(props = {}) {
  const requestId = props.requestId || null;
  const copyFromId = props.copyFromId || null;
  const targetReporterProfileId = props.targetReporterProfileId || null;
  const {
 returnUrl, viewRequestUrlTemplate, initialTitle, initialDetails,
} = props;

  // Read query parameters for prefill (only for new requests)
  const urlParams = new URLSearchParams(window.location.search);

  const prefillOverrides = {};
  if (initialTitle) prefillOverrides.title = initialTitle;
  if (initialDetails) prefillOverrides.details = initialDetails;

  // We are deliberately doing nothing for unhandled cases, hence:
  // eslint-disable-next-line default-case
  switch (urlParams.get('looking_for')) {
    case 'experts':
      prefillOverrides.lookingFor = 'person';
      break;

    case 'products':
      prefillOverrides.lookingFor = 'product';
      break;
  }

  const {
    abandonChangesAndExit,
    backToEditing,
    derivedParams,
    postRequest,
    publishNow,
    retryLoad,
    saveDraft,
    saveAndExit,
    tickTime,
    toggleParam,
    updateParam,
    useUpdateParamWithEvent,
    markFieldTouched,
  } = useSyncedState(
    requestId,
    copyFromId,
    targetReporterProfileId,
    returnUrl,
    viewRequestUrlTemplate,
    prefillOverrides,
  );

  const { currentUiState } = derivedParams;

  useEffect(() => {
    tickTime(secondsSinceEpoch());
    const intervalId = setInterval(() => {
      tickTime(secondsSinceEpoch());
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [tickTime]);

  // Render loading state while fetching request data in edit mode
  if (currentUiState === 'loading') {
    return (
      <div className="new-request-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading request...</p>
        </div>
      </div>
    );
  }

  // Render error state if loading failed
  if (currentUiState === 'loading-failed') {
    return (
      <div className="new-request-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <p className="text-danger mb-3">
            <i className="fa-solid fa-circle-exclamation me-2" />
            Something went wrong loading this request.
          </p>
          <p className="text-muted mb-3">
            If this keeps happening, please reach out to{' '}
            <a href="mailto:support@qwoted.com">support@qwoted.com</a>.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <button className="btn btn-outline-primary" type="button" onClick={retryLoad}>
              <i className="fa-solid fa-arrow-rotate-right me-2" />
              Retry
            </button>
            <a className="btn btn-outline-secondary" href={returnUrl}>
              Back to My Requests
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (currentUiState === 'reviewing') {
    if (V1_FEATURES.SKIP_REVIEW_AND_THANKS_PAGES) {
      return <RedirectSpinner />;
    }
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    return <ReviewPage {...derivedParams} backToEditing={backToEditing} publishNow={publishNow} />;
  }

  if (currentUiState === 'posted') {
    if (V1_FEATURES.SKIP_REVIEW_AND_THANKS_PAGES) {
      return <RedirectSpinner />;
    }
    return (
      <ThanksPage
        slug={derivedParams.slug}
        migrateId={derivedParams.migrateId}
        viewRequestUrlTemplate={derivedParams.viewRequestUrlTemplate}
        schedule={derivedParams.schedule}
        customScheduleDateTime={derivedParams.customScheduleDateTime}
        timeNow={derivedParams.timeNow}
      />
    );
  }

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <NewRequestBody
      {...derivedParams}
      internalUser={props.internalUser}
      targetReporterProfileId={targetReporterProfileId}
      targetUserName={props.targetUserName}
      abandonChangesAndExit={abandonChangesAndExit}
      postRequest={postRequest}
      saveDraft={saveDraft}
      saveAndExit={saveAndExit}
      toggleParam={toggleParam}
      updateParam={updateParam}
      useUpdateParamWithEvent={useUpdateParamWithEvent}
      markFieldTouched={markFieldTouched}
    />
  );
  /* eslint-enable react/jsx-props-no-spreading */
}

NewRequest.propTypes = {
  requestId: PropTypes.number,
  copyFromId: PropTypes.number,
  targetReporterProfileId: PropTypes.number,
  targetUserName: PropTypes.string,
  returnUrl: PropTypes.string,
  viewRequestUrlTemplate: PropTypes.string,
  initialTitle: PropTypes.string,
  initialDetails: PropTypes.string,
  internalUser: PropTypes.bool,
};

NewRequest.defaultProps = {
  requestId: null,
  copyFromId: null,
  targetReporterProfileId: null,
  targetUserName: null,
  returnUrl: '/reporter_requests',
  viewRequestUrlTemplate: null,
  initialTitle: null,
  initialDetails: null,
  internalUser: false,
};

function NewRequestWithSyncGate(props) {
  return (
    <V2SyncGate>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <NewRequest {...props} />
    </V2SyncGate>
  );
}

export default NewRequestWithSyncGate;
